import type { LucideIcon } from 'lucide-react'
import type { Capability } from '@region13/shared-types'

/** One nav leaf. `cap` is any-of (absent means everyone signed in); `group` is
 *  a NavGroup key (absent means the main list); `bandStart` draws a hairline
 *  above the item. An app may extend this with fields of its own (the console's
 *  `blurb`), and the model carries them through. */
/** The heading and tab-title label of the not-found page (#856). */
export const NOT_FOUND_HEADING = 'Page not found'

export interface NavItem<P extends string = string> {
  to: P
  label: string
  icon: LucideIcon
  cap?: Capability | Capability[]
  group?: string
  bandStart?: boolean
}

export interface NavGroup {
  key: string
  label: string
  icon?: LucideIcon
}

export interface NavModel<I extends NavItem = NavItem> {
  /** 'Stats', 'Console', ...: the tab title suffix, "Region 13 {app}". */
  app: string
  /** In display order. */
  items: I[]
  /** Headings, in order. */
  groups: NavGroup[]
  /** Child route id (one card, one review) -> the nav path its breadcrumb
   *  points at, or null for none. A child page takes its heading from data. */
  childRoutes: Record<string, string | null>
}

export interface NavEntry<I extends NavItem = NavItem> {
  item: I
  group: NavGroup | null
}

/** One app's nav list: the only place its labels, paths, capabilities and
 *  groups are written. Menus, page headings (useNavTitle) and tab titles all
 *  read it, and each app's nav drift test fails CI on drift. */
export function defineNav<I extends NavItem>(m: NavModel<I>): NavModel<I> {
  return m
}

/** A route id with any trailing slash dropped ('/reviews/' -> '/reviews'), except '/'. */
export function normalizeRouteId(id: string): string {
  return id.length > 1 ? id.replace(/\/+$/, '') : id
}

export function navEntryFor<I extends NavItem>(m: NavModel<I>, routeId: string): NavEntry<I> | null {
  const id = normalizeRouteId(routeId)
  const item = m.items.find((i) => i.to === id)
  if (!item) return null
  return { item, group: m.groups.find((g) => g.key === item.group) ?? null }
}

export function itemVisible(item: NavItem, can: (c: Capability) => boolean): boolean {
  if (!item.cap) return true
  return (Array.isArray(item.cap) ? item.cap : [item.cap]).some(can)
}

export interface VisibleNav<I extends NavItem = NavItem> {
  main: I[]
  groups: { group: NavGroup; items: I[] }[]
}

/** What one user sees: the main list and the non-empty groups, in order. */
export function visibleNav<I extends NavItem>(m: NavModel<I>, can: (c: Capability) => boolean): VisibleNav<I> {
  const vis = m.items.filter((i) => itemVisible(i, can))
  return {
    main: vis.filter((i) => !i.group),
    groups: m.groups
      .map((group) => ({ group, items: vis.filter((i) => i.group === group.key) }))
      .filter((g) => g.items.length > 0),
  }
}

/** A nav item's label, by path, for breadcrumbs, back links and cards, so
 *  they follow the list. Throws on a path the list does not hold. */
export function navLabel<I extends NavItem>(m: NavModel<I>, to: I['to']): string {
  const item = m.items.find((i) => i.to === to)
  if (!item) throw new Error(`No ${m.app} nav item at ${to}`)
  return item.label
}
